import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Post, slugify } from "@/hooks/usePosts";
import RichTextEditor from "./RichTextEditor";
import { Upload, ArrowLeft } from "lucide-react";

interface PostEditorProps {
  post: Post | null;
  authorMemberId: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const PostEditor = ({ post, authorMemberId, onClose, onSaved }: PostEditorProps) => {
  const [title, setTitle] = useState(post?.title ?? "");
  const [slug, setSlug] = useState(post?.slug ?? "");
  const [excerpt, setExcerpt] = useState(post?.excerpt ?? "");
  const [body, setBody] = useState(post?.body_markdown ?? "");
  const [coverImage, setCoverImage] = useState(post?.cover_image ?? "");
  const [chapter, setChapter] = useState<"washington" | "union" | "all">(post?.chapter ?? "all");
  const [category, setCategory] = useState(post?.category ?? "other");
  const [status, setStatus] = useState<"draft" | "published">(post?.status ?? "draft");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Auto-slug from title for new posts
  useEffect(() => {
    if (!post && title) setSlug(slugify(title));
  }, [title, post]);

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${Date.now()}-${slugify(title || "cover")}.${ext}`;
      const { error } = await supabase.storage
        .from("post-covers")
        .upload(path, file, { upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from("post-covers").getPublicUrl(path);
      setCoverImage(data.publicUrl);
      toast.success("Cover image uploaded");
    } catch (err: any) {
      toast.error(err.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim() || !slug.trim()) {
      toast.error("Title and slug are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        slug: slug.trim(),
        excerpt: excerpt.trim() || null,
        body_markdown: body,
        cover_image: coverImage || null,
        chapter,
        category,
        status,
        author_id: post?.author_id ?? authorMemberId,
      };

      if (post) {
        const { error } = await supabase.from("posts").update(payload).eq("id", post.id);
        if (error) throw error;
        toast.success("Post updated");
      } else {
        const { error } = await supabase.from("posts").insert(payload);
        if (error) throw error;
        toast.success("Post created");
      }
      onSaved();
    } catch (err: any) {
      toast.error(err.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            {post ? "Edit Post" : "New Post"}
          </CardTitle>
          <Badge variant={status === "published" ? "default" : "secondary"}>{status}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Story title" />
          </div>
          <div>
            <Label htmlFor="slug">URL Slug</Label>
            <Input id="slug" value={slug} onChange={(e) => setSlug(slugify(e.target.value))} placeholder="story-url-slug" />
          </div>
        </div>

        <div>
          <Label htmlFor="excerpt">Excerpt (preview text)</Label>
          <Textarea id="excerpt" value={excerpt ?? ""} onChange={(e) => setExcerpt(e.target.value)} rows={2} placeholder="Short summary shown on the stories list" />
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <Label>Chapter</Label>
            <Select value={chapter} onValueChange={(v: any) => setChapter(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Chapters</SelectItem>
                <SelectItem value="washington">Washington</SelectItem>
                <SelectItem value="union">Union</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fundraising">Fundraising</SelectItem>
                <SelectItem value="community-service">Community Service</SelectItem>
                <SelectItem value="collaboration">Collaboration</SelectItem>
                <SelectItem value="education">Education</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={(v: any) => setStatus(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Cover Image</Label>
          <div className="flex flex-col sm:flex-row gap-3 items-start">
            {coverImage && (
              <img src={coverImage} alt="Cover" className="w-32 h-20 object-cover rounded border" />
            )}
            <div className="flex-1 w-full space-y-2">
              <Input value={coverImage} onChange={(e) => setCoverImage(e.target.value)} placeholder="Cover image URL or upload below" />
              <div>
                <input type="file" accept="image/*" id="cover-upload" className="hidden" onChange={handleCoverUpload} />
                <Button type="button" variant="outline" size="sm" disabled={uploading} asChild>
                  <label htmlFor="cover-upload" className="cursor-pointer">
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Uploading..." : "Upload Cover"}
                  </label>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="body">Body</Label>
          <RichTextEditor
            value={body}
            onChange={setBody}
            placeholder="Write your story. Use the toolbar to format text, add headings, lists, links, and images."
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : post ? "Update Post" : "Create Post"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PostEditor;
